import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";

import styles from "./ActivityDetailsDialog.module.css";
import { Datum } from "../../interface/CustomActivity";

/* ─── types ─── */
interface ActivityGroup {
  name: string;
  isActive: boolean;
  isSoftDeleted?: boolean;
  id?: string | number;
}

interface TypeConfigEntry {
  label: string;
  dot: string;
  labelColor: string;
  countBg: string;
  pillCls: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentOpenItem: Datum | undefined;
  search?: string;
  totalCount?: number;
}

/* ─── colour tokens per code type ─── */
const TYPE_CONFIG: Record<string, TypeConfigEntry> = {
  CPT:     { label: "CPT",     dot: "#378ADD", labelColor: "#185FA5", countBg: "#378ADD", pillCls: styles.pillCpt },
  HCPCS:   { label: "HCPCS",   dot: "#1D9E75", labelColor: "#0F6E56", countBg: "#1D9E75", pillCls: styles.pillHcpcs },
  DENTAL:  { label: "Dental",  dot: "#7F77DD", labelColor: "#3C3489", countBg: "#7F77DD", pillCls: styles.pillDental },
  SERVICE: { label: "Service", dot: "#BA7517", labelColor: "#854F0B", countBg: "#BA7517", pillCls: styles.pillService },
};

/* ─── highlight helper ─── */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "#FAC775", color: "#412402", borderRadius: 2, padding: "0 1px" }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function ActivityDetailsDialog({
  open,
  onOpenChange,
  currentOpenItem,
  search = "",
  totalCount = 0,
}: Props): React.ReactElement | null {
  if (!currentOpenItem) return null;

  /* ─── grouped codes filtered by search ─── */
  const grouped: Record<string, string[]> = {
    CPT:     (currentOpenItem.cptCodes     ?? []).filter((c) => c.toLowerCase().includes(search.toLowerCase())),
    HCPCS:   (currentOpenItem.hecpcsCodes  ?? []).filter((c) => c.toLowerCase().includes(search.toLowerCase())),
    DENTAL:  (currentOpenItem.dentalCodes  ?? []).filter((c) => c.toLowerCase().includes(search.toLowerCase())),
    SERVICE: (currentOpenItem.serviceCodes ?? []).filter((c) => c.toLowerCase().includes(search.toLowerCase())),
  };
  const totalCodes = Object.values(grouped).reduce((s, a) => s + a.length, 0);

  const medGroups = (currentOpenItem.medicalActivityGroups ?? []).filter((g: ActivityGroup) =>
    g.name.toLowerCase().includes(search.toLowerCase()),
  );
  const custGroups = (currentOpenItem.customActivityGroups ?? []).filter((g: ActivityGroup) =>
    g.name.toLowerCase().includes(search.toLowerCase()),
  );
  const hasAnything = totalCodes > 0 || medGroups.length > 0 || custGroups.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="activity-details-dialog-content"
        className={styles.dialog}
        style={{ padding: 0, border: "none", borderRadius: 20, overflow: "hidden", maxWidth: 420 }}
      >
        {/* ── Header ── */}
        <DialogHeader className={styles.header} data-testid="activity-details-dialog-header">
          <div className={styles.iconBox} aria-hidden="true" data-testid="activity-details-dialog-icon">
            <i className="ti ti-list-check" />
          </div>
          <div className={styles.headerText}>
            <DialogTitle className={styles.title} data-testid="activity-details-dialog-title">
              Activity Details
            </DialogTitle>
            {search && (
              <span className={styles.filterTag} data-testid="activity-details-dialog-filter-tag">
                <i className="ti ti-filter" style={{ fontSize: 8, verticalAlign: 1 }} />
                {search}
              </span>
            )}
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <div className={styles.body} data-testid="activity-details-dialog-body">
          {!hasAnything ? (
            <div className={styles.emptyState} data-testid="activity-details-dialog-empty-state">
              <i className={`ti ti-search ${styles.emptyIcon}`} />
              <p className={styles.emptyText} data-testid="activity-details-dialog-empty-text">
                No matching activities found
              </p>
            </div>
          ) : (
            <>
              {/* ── Codes ── */}
              {totalCodes > 0 && (
                <div data-testid="activity-details-codes-section">
                  <div className={styles.sectionLabel}>
                    <span className={styles.sectionName}>Codes</span>
                    <span
                      className={styles.countPill}
                      style={{ background: "#E6F1FB", color: "#0C447C" }}
                      data-testid="activity-details-total-codes-count"
                    >
                      {totalCodes}
                    </span>
                  </div>

                  <div className={styles.codesGroup} data-testid="activity-details-codes-group">
                    {Object.entries(grouped).map(([typeKey, codes]) => {
                      if (codes.length === 0) return null;
                      const cfg = TYPE_CONFIG[typeKey];
                      return (
                        <div
                          key={typeKey}
                          className={styles.typeBlock}
                          data-testid={`activity-details-type-block-${typeKey.toLowerCase()}`}
                        >
                          <div className={styles.typeHeader}>
                            <div className={styles.typeHeaderLeft}>
                              <div className={styles.typeDot} style={{ background: cfg.dot }} />
                              <span className={styles.typeLabel} style={{ color: cfg.labelColor }}>
                                {cfg.label}
                              </span>
                            </div>
                            <span
                              className={styles.typeCount}
                              style={{ background: cfg.countBg }}
                              data-testid={`activity-details-type-count-${typeKey.toLowerCase()}`}
                            >
                              {codes.length}
                            </span>
                          </div>

                          <div className={styles.pillsWrap} data-testid={`activity-details-pills-wrap-${typeKey.toLowerCase()}`}>
                            {codes.map((code, i) => (
                              <span
                                key={i}
                                className={`${styles.codePill} ${cfg.pillCls}`}
                                data-testid={`activity-details-code-pill-${typeKey.toLowerCase()}-${i}`}
                              >
                                <span className={styles.pillHash}>#</span>
                                {highlightMatch(code, search)}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Medical Groups ── */}
              {medGroups.length > 0 && (
                <div data-testid="activity-details-med-groups-section">
                  <div className={styles.sectionLabel}>
                    <span className={styles.sectionName}>Medical Groups</span>
                    <span
                      className={styles.countPill}
                      style={{ background: "#E1F5EE", color: "#085041" }}
                      data-testid="activity-details-med-groups-count"
                    >
                      {medGroups.length}
                    </span>
                  </div>
                  <div className={styles.groupRows} data-testid="activity-details-med-group-rows">
                    {medGroups.map((group: ActivityGroup, i: number) => (
                      <div
                        key={`med-${i}`}
                        data-testid={`activity-details-med-group-row-${i}`}
                        className={`${styles.groupRow} ${!group.isActive ? styles.groupRowInactive : ""}`}
                      >
                        <div
                          className={`${styles.groupIcon} ${
                            !group.isActive ? styles.groupIconInactive : styles.groupIconMed
                          }`}
                        >
                          <i className="ti ti-stack-2" aria-hidden="true" />
                        </div>
                        <div>
                          <p
                            className={`${styles.groupName} ${!group.isActive ? styles.groupNameInactive : ""}`}
                            data-testid={`activity-details-med-group-name-${i}`}
                          >
                            {highlightMatch(group.name, search)}
                          </p>
                          {!group.isActive && (
                            <p
                              className={styles.inactiveBadge}
                              data-testid={`activity-details-med-group-status-inactive-${i}`}
                            >
                              Inactive
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Custom Groups ── */}
              {custGroups.length > 0 && (
                <div className="space-y-3" data-testid="activity-details-cust-groups-section">
                  <div className="flex items-center gap-2 ml-1">
                    <h4 className="text-[11px] font-bold text-slate-500 flex items-center gap-2 normal-case">
                      <i className="fa-solid fa-folder-tree text-violet-500"></i>
                      Custom Groups
                    </h4>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.2 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100"
                      data-testid="activity-details-cust-groups-count"
                    >
                      {custGroups.length}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2" data-testid="activity-details-cust-group-list">
                    {custGroups.map((group: ActivityGroup, index: number) => {
                      const isDeleted = group.isSoftDeleted;
                      const isInactive = !group.isActive;

                      return (
                        <div
                          key={`cust-${index}`}
                          data-testid={`activity-details-cust-group-row-${index}`}
                          className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                            isDeleted
                              ? "bg-red-50/40 opacity-45 blur-[0.3px] line-through border-red-200 text-red-500 decoration-red-400"
                              : isInactive
                              ? "bg-slate-50 opacity-60 grayscale border-slate-200 border-dashed"
                              : "bg-violet-50/50 border-violet-100"
                          }`}
                        >
                          <div
                            className={`flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0 text-white shadow-sm transition-all ${
                              isDeleted
                                ? "bg-red-500"
                                : isInactive
                                ? "bg-slate-400"
                                : "bg-violet-600"
                            }`}
                          >
                            <i
                              className={`fa-solid ${
                                isDeleted ? "fa-trash-can" : "fa-folder-tree"
                              } text-[10px]`}
                            ></i>
                          </div>

                          <span
                            className={`text-sm font-semibold flex-1 ${
                              isDeleted ? "text-red-900" : "text-gray-800"
                            }`}
                            data-testid={`activity-details-cust-group-name-${index}`}
                          >
                            {highlightMatch(group.name, search)}
                          </span>

                          {isDeleted ? (
                            <span
                              className="text-[9px] font-extrabold bg-red-100 text-red-600 px-2 py-0.5 rounded flex items-center gap-1 tracking-wider border border-red-200"
                              data-testid={`activity-details-cust-group-status-deleted-${index}`}
                            >
                              <i className="fa-solid fa-trash-can"></i> DELETED
                            </span>
                          ) : (
                            isInactive && (
                              <span
                                className="text-[9px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded flex items-center gap-1"
                                data-testid={`activity-details-cust-group-status-inactive-${index}`}
                              >
                                <i className="fa-solid fa-eye-slash"></i> INACTIVE
                              </span>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer} data-testid="activity-details-dialog-footer">
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Total linked</span>
            <span className={styles.summaryCount} data-testid="activity-details-summary-count">
              {totalCount} items
            </span>
          </div>
          <Button
            className={styles.closeBtn}
            onClick={() => onOpenChange(false)}
            data-testid="activity-details-dialog-close-btn"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
